import React, { useRef, useCallback, useState, useMemo } from "react";
import { Attachments } from "@ant-design/x";
import type { UploadFile } from "antd";
import { useChatState } from "../context/ChatStateContext";
import { useChatSenderState } from "../context/ChatSenderStateContext";
import { DefaultLayoutContext } from "../context/DefaultLayoutContext";
import Sidebar from "./Sidebar";
import Messages from "./Messages";
import Welcome from "./Welcome";
import Sender from "./Sender";
import { useStyles } from "./styles";
import type { ChatStrategies } from "@/types";
import type { XAdkSenderHandle } from "@/types/XAdkSender";

/**
 * XAdkProvider 默认布局
 *
 * 提供类似 ChatGPT/DeepSeek 的默认布局:
 * - 左侧: 会话列表侧边栏 (可选,支持展开/收起)
 * - 右侧: 聊天区域 (标题 + 消息 + 输入框,居中显示,最大宽度800px)
 */
export interface DefaultLayoutProps {
  /** 是否显示会话列表 */
  showSessionList?: boolean;
  /** 自定义头部 */
  header?: React.ReactNode;
  /** 自定义底部 (替代默认 Sender) */
  footer?: React.ReactNode;
  /**
   * 是否启用内置上传入口（上传按钮 + 隐藏 file input + 拖拽）。
   * 未传时继承 XAdkProvider config.allowUpload。
   * 内核 XAdkSender 不再内置上传逻辑，上传能力统一由 DefaultLayout 管理。
   * 业务方自定义 footer 时可自行控制上传。
   */
  allowUpload?: boolean;
  /** 是否启用拖拽上传；未传时继承 config.draggable */
  draggable?: boolean;
  /** 是否显示清空按钮 */
  clearBtnShow?: boolean;
  /** 是否显示函数调用详情 */
  showFnCallDetail?: boolean;
  /** 自定义消息 Footer */
  messagesFooter?: (msg: any) => React.ReactNode;
  /** 自定义工具调用渲染函数，返回 null 则降级使用默认渲染 */
  renderFunctionCall?: (
    msg: import("@/types").IMessage,
  ) => React.ReactNode | null;
  /** 局部策略覆盖，优先级高于 Provider preset/strategies */
  strategies?: ChatStrategies;
}

const DefaultLayout: React.FC<DefaultLayoutProps> = ({
  showSessionList = false,
  header,
  footer,
  allowUpload,
  draggable,
  clearBtnShow = true,
  showFnCallDetail,
  renderFunctionCall,
  strategies,
}) => {
  const { appInfo, messages, prologue, suggestions } = useChatState();
  const { allowUpload: providerAllowUpload, draggable: providerDraggable } =
    useChatSenderState();
  // Layout props 用于局部覆盖；未传时继承 Provider config，保证 NPM 与 UMD 接入一致。
  const mergedAllowUpload = allowUpload ?? providerAllowUpload;
  const mergedDraggable = draggable ?? providerDraggable;
  const styles = useStyles();
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const senderRef = useRef<XAdkSenderHandle>(null);
  const [dropFileList, setDropFileList] = useState<UploadFile[]>([]);
  const registerSender = useCallback((sender: XAdkSenderHandle | null) => {
    senderRef.current = sender;
  }, []);
  const layoutContextValue = useMemo(
    () => ({ allowUpload: mergedAllowUpload, registerSender }),
    [mergedAllowUpload, registerSender],
  );

  // 拖拽上传只有在上传能力开启时生效，避免关闭上传后仍响应文件拖入。
  const enableDragUpload = mergedAllowUpload && mergedDraggable;

  // 文件注入到 Sender（按钮点击 + 拖拽统一走这里）
  const handleFiles = useCallback((files: File[]) => {
    if (files.length > 0) {
      senderRef.current?.addFiles(files);
    }
  }, []);

  // Attachments 拖拽文件后的回调
  const handleDropFiles = useCallback(
    (info: { fileList: UploadFile[] }) => {
      const rawFiles = info.fileList
        .map((f) => f.originFileObj)
        .filter(Boolean) as File[];
      handleFiles(rawFiles);
      // 清空 Attachments 文件列表，避免累积
      setDropFileList([]);
    },
    [handleFiles],
  );

  // 判断是否为空状态(展示Welcome)
  const isEmpty =
    messages.length === 0 &&
    !prologue &&
    (!suggestions || suggestions.length === 0);

  return (
    <div
      style={{
        display: "flex",
        height: "100%",
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* 左侧会话列表 - XAiConversations 组件会处理展开/收起 */}
      {showSessionList && <Sidebar isNarrow={false} />}

      {/* 右侧聊天区域 - 使用 flex: 1 自动占据剩余空间 */}
      <div
        ref={chatAreaRef}
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* 居中容器 - 最大宽度 800px */}
        <div
          style={{
            width: "100%",
            maxWidth: "800px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* 头部 */}
          {header ||
            (appInfo && (
              <div
                style={{
                  padding: "16px 24px",
                  display: "flex",
                  justifyContent: "center",
                  background: "white",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  {appInfo.appName && (
                    <div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 500,
                          color: "#000",
                        }}
                      >
                        {appInfo.appName}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

          {/* 消息区域或Welcome区域 */}
          <div
            style={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* 上方弹性占位 - Welcome状态时占据空间 */}
            <div
              style={{
                flex: isEmpty ? 1 : 0,
                transition: "flex 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />

            {/* Welcome 组件 - 只在空状态显示 */}
            {isEmpty && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "0 24px",
                  marginBottom: "24px",
                }}
              >
                <Welcome />
              </div>
            )}

            {/* Messages 组件 - 只在非空状态显示 */}
            {!isEmpty && (
              <div
                style={{
                  flex: 1,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Messages
                  showFnCallDetail={showFnCallDetail}
                  renderFunctionCall={renderFunctionCall}
                  strategies={strategies}
                />
              </div>
            )}
            {/* 输入区域 - 始终存在,通过样式控制位置 */}
            <div
              className={styles.sendWrapper}
              style={{
                maxWidth: isEmpty ? "600px" : "100%",
                alignSelf: "center",
                padding: isEmpty ? "0 24px" : "0",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <DefaultLayoutContext.Provider value={layoutContextValue}>
                {footer || (
                  <Sender
                    ref={senderRef}
                    draggable={false}
                    onFileSelect={handleFiles}
                    clearBtnShow={clearBtnShow}
                  />
                )}
              </DefaultLayoutContext.Provider>
            </div>

            {/* 下方弹性占位 - Welcome状态时占据更多空间,让内容偏上 */}
            <div
              style={{
                flex: isEmpty ? 1.5 : 0,
                transition: "flex 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </div>
        </div>

        {/* Attachments 拖拽遮罩 - 仅用于拖拽上传功能 */}
        {enableDragUpload && (
          <Attachments
            style={{ display: "none" }}
            items={dropFileList}
            getDropContainer={() => chatAreaRef.current}
            multiple
            beforeUpload={() => false}
            onChange={handleDropFiles}
          />
        )}
      </div>
    </div>
  );
};

DefaultLayout.displayName = "XAdkProvider.DefaultLayout";

export default DefaultLayout;
